import { useContext, useMemo, JSX } from "react"
import { IsoDate } from "helpers/types/dates"
import { Period } from "helpers/dates"
import OPTooltip from "components/material/tooltip"
import { IDashboardContext } from "connected-components/dashboard/context/types"

import "./style.scss"

const PERIOD_OPTIONS: Record<Period, string> = {
  "1D": "1 Day",
  MTD: "Month-to-Date",
  "1M": "1 Month",
  "3M": "3 Months",
  "6M": "6 Months",
  YTD: "Year-to-Date",
  "1Y": "1 Year",
  "3Y": "3 Years",
  SAME_DAY: "Same Day",
  CUSTOM: "Custom Range",
}

function useTooltipContent(
  isRiskAdjustedDriftDate: boolean,
  adjustedStartDate: IsoDate,
  endDate: IsoDate,
  isPerformance: boolean,
  performanceStartDate: IsoDate,
  performanceEndDate: IsoDate,
  startDate: IsoDate,
) {
  return useMemo((): JSX.Element | string => {
    if (isRiskAdjustedDriftDate) {
      return (
        <>
          <div>The drift period has been adjusted to three months to get a statistically significant drift calculation.</div>
          <br />
          <div>The drift period is now</div>
          <div>{adjustedStartDate} to {endDate}.</div>
        </>
      )
    }

    const effectiveStartDate = isPerformance ? performanceStartDate : startDate
    const effectiveEndDate = isPerformance ? performanceEndDate : endDate
    return `${effectiveStartDate} to ${effectiveEndDate}`
  }, [isRiskAdjustedDriftDate, adjustedStartDate, endDate, isPerformance, performanceStartDate, performanceEndDate, startDate])
}

export default function PeriodDisplay({ context, isPerformance = true, label = "Period", driftDates }: PeriodDisplayProps) {
  const { period, endDate, startDate, performanceStartDate, performanceEndDate } = useContext(context)

  const [adjustedStartDate, isRiskAdjustedDriftDate] = useMemo((): [IsoDate, boolean] => {
    if (isPerformance) {
      return [performanceStartDate, false]
    }
    if (!driftDates) {
      return [startDate, false]
    }
    if (driftDates.startDate < startDate) {
      return [driftDates.startDate, true]
    }
    return [startDate, false]
  }, [driftDates, startDate, isPerformance, performanceStartDate])

  const periodOptionText = useMemo(() => {
    if (isRiskAdjustedDriftDate) {
      return PERIOD_OPTIONS["3M"]
    }

    return PERIOD_OPTIONS[period] || "Custom Range"
  }, [period, isRiskAdjustedDriftDate])

  const tooltipContent = useTooltipContent(
    isRiskAdjustedDriftDate,
    adjustedStartDate,
    endDate,
    isPerformance,
    performanceStartDate,
    performanceEndDate,
    startDate,
  )

  return (
    <div className="period-display">
      <span className="period-display-label">{label}</span>
      <OPTooltip
        title={tooltipContent}
        placement="top"
      >
        <span className="period-display-value">
          <span>{periodOptionText}</span>
        </span>
      </OPTooltip>
    </div>
  )
}

interface PeriodDisplayProps {
  context: IDashboardContext
  isPerformance?: boolean
  label?: "Period" | "Drift Period"
  driftDates?: {
    startDate: IsoDate
    endDate: IsoDate
  }
}
